import { reactive } from 'vue';
import sdk from '@/sdk';

export class DataSchema {
  feta_collective_synthesis?: any;
  ai_adoption_collective_synthesis?: any;
  skills_collective_synthesis?: any;
  [key: string]: any; // other <x>_collective_synthesis siblings (e.g. custom behaviors-model data)
}

export class Specification {
  feta_collective_synthesis?: string;
  skills_collective_synthesis?: string;
  ai_adoption_collective_synthesis?: string;
}

export type CollectiveReportName =
  'feta_collective_synthesis' | 'skills_collective_synthesis' | 'ai_adoption_collective_synthesis';

export type Operator = '=' | '>' | '<' | '>=' | '<=';

export class DataStore {
  public uuid?: string;
  public isLoaded: boolean;
  public evaluated_group: DataSchema | undefined;
  public reference: DataSchema | undefined;
  private promise: undefined | Promise<void>;
  public selectedModel: string;
  public specification?: Specification;
  public showDebugVersion: boolean = false;

  constructor() {
    this.uuid = undefined;
    this.isLoaded = false;
    this.evaluated_group = undefined;
    this.reference = undefined;
    this.promise = undefined;
    this.selectedModel = 'global';
  }

  async loadData(uuid: string): Promise<void> {
    this.uuid = uuid;

    if (!this.uuid) return;
    if(this.isLoaded) return this.promise

    this.promise = (async () => {
      try {
        const response = await sdk.getAnalysisData(this.uuid!);

        this.specification = {};
        if (Object.hasOwn(response.data.collective_report, 'feta_collective_synthesis')) {
          this.specification.feta_collective_synthesis =
            response.data.collective_report.feta_collective_synthesis.version;
        }
        if (Object.hasOwn(response.data.collective_report, 'skills_collective_synthesis')) {
          this.specification.skills_collective_synthesis =
            response.data.collective_report.skills_collective_synthesis.version;
        }
        if (Object.hasOwn(response.data.collective_report, 'ai_adoption_collective_synthesis')) {
          this.specification.ai_adoption_collective_synthesis =
            response.data.collective_report.ai_adoption_collective_synthesis.version;
        }

        this.evaluated_group = {};
        if (Object.hasOwn(response.data.collective_report, 'ai_adoption_collective_synthesis'))
          this.evaluated_group.ai_adoption_collective_synthesis =
            response.data.collective_report.ai_adoption_collective_synthesis.evaluated_group;

        this.reference = {};
        if (Object.hasOwn(response.data.collective_report, 'feta_collective_synthesis')) {
          const feta = response.data.collective_report.feta_collective_synthesis;
          this.evaluated_group.feta_collective_synthesis = feta.evaluated_group;
          this.reference.feta_collective_synthesis = feta.reference;
        }

        if (Object.hasOwn(response.data.collective_report, 'skills_collective_synthesis')) {
          const skills = response.data.collective_report.skills_collective_synthesis;
          const evaluatedGroup = skills.evaluated_group;
          if (evaluatedGroup?.individuals_data)
            this.evaluated_group.skills_collective_synthesis = {
              individuals_data: evaluatedGroup.individuals_data,
              aggregated_data: evaluatedGroup.aggregated_data,
            };
          this.reference.skills_collective_synthesis = skills.reference;
        }

        const knownReports = [
          'feta_collective_synthesis',
          'skills_collective_synthesis',
          'ai_adoption_collective_synthesis',
        ];
        for (const key of Object.keys(response.data.collective_report)) {
          if (key.endsWith('_collective_synthesis') && !knownReports.includes(key)) {
            this.evaluated_group[key] = response.data.collective_report[key].evaluated_group;
          }
        }

        this.isLoaded = true;
      } catch (error) {
        this.evaluated_group = undefined;
        this.reference = undefined;
        throw error;
      } finally {
        this.promise = undefined;
      }
    })();

    return this.promise;
  }

  get hasGroupData() {
    return this.evaluated_group?.feta_collective_synthesis !== undefined;
  }

  get hasAIGroupData() {
    return this.evaluated_group?.ai_adoption_collective_synthesis !== undefined;
  }

  get hasIndividualData() {
    return this.evaluated_group?.skills_collective_synthesis !== undefined;
  }

  get isResources() {
    return !this.hasGroupData && this.hasIndividualData;
  }

  get isResourcesOnly() {
    return !this.hasGroupData && !this.hasAIGroupData && this.hasIndividualData;
  }

  get isAI() {
    return this.hasAIGroupData && this.hasIndividualData;
  }

  get hasIndividualSubj() {
    if (!this.hasIndividualData) return false;

    let hasSubj = false;

    for (const item of this.evaluated_group?.skills_collective_synthesis?.individuals_data ?? []) {
      for (const skillCat in item.skills) {
        if (Object.keys(item.skills[skillCat]).length > 0) {
          for (const skill in item.skills[skillCat]) {
            if (Object.hasOwn(item.skills[skillCat][skill], 'subj')) {
              hasSubj = true;
            }
          }
        }
      }
    }

    return hasSubj;
  }

  hasSkillSubj(skillKey: string) {
    if (!this.hasIndividualData) return false;

    let hasSubj = false;

    const temp = skillKey.split('_');
    const skillCat = temp[0];
    const skillName = skillKey.replace(skillCat + '_', '');

    for (const item of this.evaluated_group?.skills_collective_synthesis?.individuals_data ?? []) {
      if (Object.hasOwn(item.skills, skillCat)) {
        if (Object.hasOwn(item.skills[skillCat], skillName)) {
          if (Object.hasOwn(item.skills[skillCat][skillName], 'subj')) {
            hasSubj = true;
          }
        }
      }
    }

    return hasSubj;
  }

  hasSkill(skillKey: string) {
    if (!this.hasIndividualData) return false;

    let hasSkill = false;

    const temp = skillKey.split('_');
    const skillCat = temp[0];
    const skillName = skillKey.replace(skillCat + '_', '');

    for (const item of this.evaluated_group?.skills_collective_synthesis?.individuals_data ?? []) {
      if (Object.hasOwn(item.skills, skillCat)) {
        if (Object.hasOwn(item.skills[skillCat], skillName)) {
          hasSkill = true;
        }
      }
    }

    return hasSkill;
  }

  matchVersion(report: CollectiveReportName, version: string, operator: Operator) {
    if (!this.specification) return false;
    const specVersion = this.specification[report];
    if (!specVersion) return false;

    const comparison = this._compareVersions(specVersion, version);

    switch (operator) {
      case '=':
        return comparison === 0;
      case '>':
        return comparison > 0;
      case '<':
        return comparison < 0;
      case '>=':
        return comparison >= 0;
      case '<=':
        return comparison <= 0;
      default:
        throw new Error(`Opérateur non valide : ${operator}`);
    }
  }

  _compareVersions(v1: string, v2: string): number {
    const parts1 = v1.split('.').map(Number);
    const parts2 = v2.split('.').map(Number);
    const maxLength = Math.max(parts1.length, parts2.length);

    for (let i = 0; i < maxLength; i++) {
      const num1 = parts1[i] || 0;
      const num2 = parts2[i] || 0;

      if (num1 > num2) return 1;
      if (num1 < num2) return -1;
    }

    return 0;
  }
}

let dataStore: DataStore;

export function useDataStore(): DataStore {
  if (!dataStore) dataStore = new DataStore();

  return reactive(dataStore) as DataStore;
}

export function getCategoryFromKey(key: string): string {
  return key.split('_')[0];
}
